class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleCartDrawerClick.bind(this));
    this.addEventListener('change', this.handleCartDrawerChange.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  handleCartDrawerClick(event) {
    const addButton = event.target.closest('[data-cart-drawer-add-variant]');
    if (!addButton) return;

    event.preventDefault();
    const variantId = addButton.dataset.cartDrawerAddVariant;
    if (!variantId) return;
    const sourceCollection = addButton.dataset.cartDrawerSourceCollection;
    const productHandle = addButton.dataset.cartDrawerProductHandle;
    const properties = {};
    if (sourceCollection) properties._cart_drawer_source_collection = sourceCollection;
    if (productHandle) properties._cart_drawer_product_handle = productHandle;

    addButton.setAttribute('aria-busy', 'true');
    addButton.disabled = true;

    fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors || !parsedState.sections) {
          throw new Error(parsedState.description || parsedState.message || 'Could not add this item to cart.');
        }
        this.renderContents(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: parsedState, variantId });
      })
      .catch((error) => {
        console.error(error);
        const errorContainer = this.querySelector('#CartDrawer-CartErrors');
        if (errorContainer) errorContainer.textContent = error.message;
      })
      .finally(() => {
        addButton.removeAttribute('aria-busy');
        addButton.disabled = false;
      });
  }

  handleCartDrawerChange(event) {
    const giftWrapToggle = event.target.closest('[data-gift-wrap-toggle]');
    if (!giftWrapToggle) return;

    const variantId = giftWrapToggle.dataset.giftWrapVariantId;
    if (!variantId) {
      giftWrapToggle.checked = false;
      giftWrapToggle.disabled = true;
      return;
    }

    giftWrapToggle.disabled = true;
    const updates = {};
    updates[variantId] = giftWrapToggle.checked ? 1 : 0;

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors || !parsedState.sections) {
          throw new Error(parsedState.description || parsedState.message || 'Could not update gift wrapping.');
        }
        this.renderContents(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: parsedState, variantId });
      })
      .catch((error) => {
        console.error(error);
        giftWrapToggle.checked = !giftWrapToggle.checked;
      })
      .finally(() => {
        giftWrapToggle.disabled = false;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.id]) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
